/* eslint-disable boundaries/element-types */
import { InfoCircleOutlined } from '@ant-design/icons'
import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigate } from '@tanstack/react-router'
import { Checkbox, message, Tooltip } from 'antd'
import { useSalesforceAccountsForOrganization } from 'components/rightDrawer/drawerStates/crudWorkflows/utils/salesforceData'
import {
  formValidationStyles,
  StyledFormInput,
  StyledFormSubmitButton,
} from 'components/rightDrawer/drawerStates/crudWorkflows/utils/utils'
import { RightSideDrawerProps } from 'components/rightDrawer/utils/interfaces'
import { useRightDrawer } from 'components/rightDrawer/utils/RightDrawerProvider'
import { SalesforceSelect } from 'components/salesforce/SalesforceSelect'
import { useRolodexEdit } from 'features/fde'
import { generateEntrySlug } from 'features/rolodex'
import React, { BaseSyntheticEvent, useEffect, useMemo, useState } from 'react'
import {
  Controller,
  FormProvider,
  SubmitHandler,
  useForm,
} from 'react-hook-form'
import styled from 'styled-components'
import { GeckoOrganization, RolodexEntryPutJson, RolodexEntryType } from 'types'
import { v4 as uuid4 } from 'uuid'

import { defaultValues, EditOrganizationSchema } from './editOrganizationSchema'

const StyledOrganizationContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  ${formValidationStyles}
`

const StyledHeaderDiv = styled.div`
  padding-bottom: 15px;
`

const StyledSelectDiv = styled.div`
  margin-bottom: 1rem;
`

export const EditGeckoOrganizationProps: RightSideDrawerProps = {
  title: 'Edit Gecko Organization',
}

const newOrgEntry = {
  id: 'test',
  type: 'organization',
  data: {},
  tags: {},
} as RolodexEntryPutJson

export const EditGeckoOrganization = () => {
  const form = useForm<EditOrganizationSchema>({
    mode: 'onBlur',
    resolver: zodResolver(EditOrganizationSchema),
    defaultValues,
  })

  const {
    watch,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = form

  const { drawerEntry, closeDrawer } = useRightDrawer()
  const { rolodexPut } = useRolodexEdit()
  const navigate = useNavigate()
  const [messageApi, contextHolder] = message.useMessage()

  const [orgSlug, setOrgSlug] = useState<string>('')
  const [showChildAccounts, setShowChildAccounts] = useState(false)

  // Only create the hash once, so it's not changing every time we type
  const orgHash = useMemo(() => uuid4().slice(0, 6), [])

  // Only update the slug if we didn't pass in an entry (since we're adding a new one)
  const updateSlug = (e: BaseSyntheticEvent) => {
    if (!drawerEntry) {
      setOrgSlug(
        e.target.value ? generateEntrySlug(e.target.value, orgHash) : ''
      )
    }
  }

  // On load, if we passed in an entry, ensure that we populate the slug field
  useEffect(() => {
    if (drawerEntry) {
      if (drawerEntry instanceof GeckoOrganization) {
        reset({
          organizationName: drawerEntry.data.name,
          salesforceAccount:
            drawerEntry.tags[RolodexEntryType.SALESFORCE_ACCOUNT],
        })
        setOrgSlug(drawerEntry.raw.id)
      } else {
        setOrgSlug(drawerEntry.id)
      }
    }
  }, [drawerEntry, reset])

  const {
    salesforceAccountOptions: unfilteredOptions,
    salesforceAccountIdToNameMap,
  } = useSalesforceAccountsForOrganization(
    drawerEntry?.raw.tags[RolodexEntryType.SALESFORCE_ACCOUNT] ?? undefined
  )

  // Filter out child accounts when checkbox is unchecked
  const salesforceAccountOptions = useMemo(() => {
    if (showChildAccounts) {
      return unfilteredOptions
    }
    return unfilteredOptions.filter((account) => account.parent === 'None')
  }, [unfilteredOptions, showChildAccounts])

  const containerTitle =
    drawerEntry instanceof GeckoOrganization ? 'Editing' : 'Adding'
  const submitButtonText =
    drawerEntry instanceof GeckoOrganization ? 'Save' : 'Create'

  const onSubmit: SubmitHandler<EditOrganizationSchema> = (data) => {
    const { organizationName, salesforceAccount: salesforceAccountId } = data

    const orgRawJSON = drawerEntry ? drawerEntry.raw : newOrgEntry

    orgRawJSON.id = orgSlug
    orgRawJSON.data.name = organizationName as string
    if (salesforceAccountId) {
      orgRawJSON.tags[RolodexEntryType.SALESFORCE_ACCOUNT] = salesforceAccountId
    }

    const action = drawerEntry ? 'modified this' : 'created new'
    rolodexPut([orgRawJSON])
      .then(() => {
        return messageApi.success(
          `Successfully ${action} organization! Redirecting...`,
          1
        )
      })
      .then(() => {
        closeDrawer()
        // TODO: ENT-1266 -- Remove the setTimeout when we are able to optimistically update
        setTimeout(
          () =>
            navigate({
              to: '/no-sites/$orgID',
              params: {
                orgID: orgRawJSON.id,
              },
            }),
          2000
        )
      })
      .catch(() => {
        messageApi.error(
          `Failed to ${action} organization! Please check permissions and try again.`
        )
      })
  }

  // Double check and make sure we got the right type of GeckoEntryBase for this
  // drawer state
  if (drawerEntry && !(drawerEntry instanceof GeckoOrganization)) {
    return (
      <div>
        Unexpected entry type. Expected GeckoOrganization, recieved{' '}
        {drawerEntry.constructor.name}
      </div>
    )
  }

  return (
    <FormProvider {...form}>
      <StyledOrganizationContainer>
        {contextHolder}
        <div>
          <StyledHeaderDiv>
            <h3>
              {containerTitle}: {watch('organizationName')}
            </h3>
            <h4>Slug: {orgSlug}</h4>
          </StyledHeaderDiv>

          <form onSubmit={handleSubmit(onSubmit)}>
            <Controller
              name='organizationName'
              control={control}
              render={({ field }) => (
                <div>
                  <label htmlFor='organizationName'>Organization Name:</label>
                  <StyledFormInput
                    id='organizationName'
                    {...field}
                    onBlur={field.onBlur}
                    ref={field.ref}
                    value={field.value}
                    autoFocus
                    onChange={(e) => {
                      updateSlug(e)
                      field.onChange(e)
                    }}
                    aria-required='true'
                    aria-invalid={!!errors.organizationName}
                  />
                </div>
              )}
            />
            {errors.organizationName && (
              <p>{errors.organizationName.message}</p>
            )}
            <Controller
              name='salesforceAccount'
              control={control}
              render={({ field: { name, onBlur, onChange, value, ref } }) => (
                <StyledSelectDiv>
                  <label htmlFor={name}>Salesforce Account</label>
                  <div
                    style={{
                      margin: '0.1rem',
                      display: 'flex',
                      alignItems: 'center',
                    }}>
                    <Checkbox
                      checked={showChildAccounts}
                      onChange={(e) => setShowChildAccounts(e.target.checked)}>
                      Show Child Accounts
                    </Checkbox>
                    <Tooltip
                      title={`Salesforce accounts have a parent/child relationship. 
                      For example, the account Koch Industries has no parent, but is the parent for the organization Georgia Pacific (Corporate), which has the child account for the site Georgia-Pacific - Brewton. For this example, if this box is unchecked, only Koch Industries will be shown as an option.`}>
                      <InfoCircleOutlined
                        style={{ marginLeft: '.1rem', cursor: 'help' }}
                      />
                    </Tooltip>
                  </div>

                  <SalesforceSelect
                    id={name}
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    ref={ref}
                    data={salesforceAccountOptions}
                    groupByParent={showChildAccounts}
                    salesforceAccountIdToNameMap={salesforceAccountIdToNameMap}
                  />
                  {errors.salesforceAccount && (
                    <span role='alert'>{errors.salesforceAccount.message}</span>
                  )}
                </StyledSelectDiv>
              )}
            />

            <StyledFormSubmitButton htmlType='submit'>
              {submitButtonText}
            </StyledFormSubmitButton>
          </form>
        </div>
      </StyledOrganizationContainer>
    </FormProvider>
  )
}
